import React from 'react';
import { ArcIconButton, ArcMenuItem, ArcProgressBar, ArcStack, ArcText, ArcTooltip } from '@src/core/ui';
import { Menu } from '@mui/material';
import { ReactComponent as ReplaceIcon } from '@src/assets/icons/replace.svg';
import { ReactComponent as AdjustIcon } from '@src/assets/icons/adjust.svg';
import { useConfirm } from '@src/core/controllers';
import { useFleetLimitedAccess } from '@src/features/fleet/context/fleet-limited-access-context';
import type { AdjustType, RuntimeItem, RuntimeItemRowProps } from '../types';

const ADJUST_OPTIONS = [
  { id: 'runtime', label: 'Adjust Runtime' },
  { id: 'serviceLimit', label: 'Adjust Service Limit' },
] as const;

const RuntimeItemRow: React.FC<RuntimeItemRowProps> = ({
  partId,
  name,
  currentHours,
  limitHours,
  hoursLabel,
  progressValue,
  type,
  onAdjustOptionClick,
  onReplaceClick,
}) => {
  const fleetLimited = useFleetLimitedAccess();
  const [adjustAnchorEl, setAdjustAnchorEl] = React.useState<null | HTMLElement>(null);
  const adjustOpen = Boolean(adjustAnchorEl);
  const item: RuntimeItem = { partId, name, currentHours, limitHours, hoursLabel, progressValue, type };

  const { confirm } = useConfirm();
  const handleReplace = () => {
    confirm(
      `Confirm that ${name} has been replaced. This action cannot be undone.`,
      'Confirm Replacement'
    ).then((result) => {
      if (result) onReplaceClick?.(item);
    });
  };
  const handleAdjustClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAdjustAnchorEl(event.currentTarget);
  };
  const handleAdjustClose = () => {
    setAdjustAnchorEl(null);
  };
  const handleAdjustOption = (optionId: string) => {
    handleAdjustClose();
    onAdjustOptionClick?.(optionId as AdjustType, item);
  };

  return (
    <ArcStack marginTop={2} direction="row" justifyContent="space-between" gap={1}>
      <ArcStack flex={1}>
        <ArcStack direction="row" alignItems="center" justifyContent="space-between">
          <ArcText color="white" fontSize={12}>{name}</ArcText>
          <ArcText color="white" fontSize={12}>{hoursLabel}</ArcText>
        </ArcStack>
        <ArcProgressBar.condition value={progressValue} sx={{ marginTop: 1 }} />
      </ArcStack>
      {!fleetLimited && (
        <ArcStack direction="row">
          <ArcTooltip title="Replacement">
            <ArcIconButton onClick={handleReplace}>
              <ReplaceIcon />
            </ArcIconButton>
          </ArcTooltip>
          <ArcTooltip title="Adjustment">
            <ArcIconButton
              onClick={handleAdjustClick}
              aria-controls={adjustOpen ? 'adjust-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={adjustOpen ? 'true' : undefined}
            >
              <AdjustIcon />
            </ArcIconButton>
          </ArcTooltip>
          <Menu
            id="adjust-menu"
            anchorEl={adjustAnchorEl}
            open={adjustOpen}
            onClose={handleAdjustClose}
            MenuListProps={{ 'aria-labelledby': 'adjust-button' }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{
              paper: { sx: { backgroundColor: 'white', minWidth: 160 } },
            }}
          >
            {ADJUST_OPTIONS.map((option) => (
              <ArcMenuItem
                key={option.id}
                onClick={() => handleAdjustOption(option.id)}
                sx={{ color: 'black', fontSize: '14px' }}
              >
                {option.label}
              </ArcMenuItem>
            ))}
          </Menu>
        </ArcStack>
      )}
    </ArcStack>
  );
};

export default RuntimeItemRow;
